import React, { useContext, useRef, useState } from 'react';
import { When } from 'react-if';

import { gql, useMutation } from '@apollo/client';

import ErrorIcon from '@nerdwallet/react-icon/nerdwallet-ui/Error';

import { colors } from '@nerdwallet/base-styles/variables';

import { Box } from '@nerdwallet/currency/components/Box';
import { CheckboxOption } from '@nerdwallet/currency/components/CheckboxOption';
import { Link } from '@nerdwallet/currency/components/Link';
import { Text } from '@nerdwallet/currency/components/Text';
import { Title } from '@nerdwallet/currency/components/Title';
import { Stack } from '@nerdwallet/currency/components/Stack';

import { Button } from '@nerdwallet/nerdwallet-plus-components';

import SegmentImpression from '@components/segment-impression';

import {
  CURRENT_CANOPY_TOS_VERSION,
  TERMS_OF_SERVICE_SOURCE,
} from '@lib/hooks/insurance/use-canopy-terms-query';
import { useIsNerdWalletPlusMember } from '@lib/hooks/nerdwallet-plus/use-is-nerdwallet-plus-member';
import { useAnalytics } from '@lib/segment';

import { Actions } from '../canopy-flow-state';
import type { Action } from '../canopy-flow-state';
import CanopyFlowContext from '../canopy-flow-context';

import styles from './terms-of-service-screen.module.scss';

type Props = {
  dispatch: React.Dispatch<Action>;
};

type AcknowledgeCanopyTermsOfServiceResponse = {
  acknowledgeCanopyTermsOfServiceAgreement?: {
    success: boolean;
  };
};

export const ACKNOWLEDGE_CANOPY_TERMS_OF_SERVICE = gql`
  mutation AcknowledgementCanopyTermsOfService(
    $source: String!
    $version: String!
  ) {
    acknowledgeCanopyTermsOfServiceAgreement(
      source: $source
      version: $version
    ) {
      success
    }
  }
`;

type AcknowledgeCoveredTermsOfServiceResponse = {
  acknowledgeCoveredTermsOfService?: {
    success: boolean;
  };
};
export const ACKNOWLEDGE_COVERED_TERMS_OF_SERVICE = gql`
  mutation AcknowledgeCoveredTermsOfService {
    acknowledgeCoveredTermsOfService {
      success
    }
  }
`;

const TermsOfServiceScreen = ({ dispatch }: Props): React.ReactNode => {
  const { segmentTrack } = useAnalytics();
  const [checked, setChecked] = useState<boolean>(false);
  const [hasError, sethasError] = useState<boolean>(false);
  const titleRef = useRef<HTMLInputElement>();
  const { isNerdWalletPlusMember } = useIsNerdWalletPlusMember();
  const { isPlatformNative } = useContext(CanopyFlowContext);

  const [
    acknowledgeCanopyTermsOfServiceMutation,
    { loading: isLoadingCanopyTermsOfService },
  ] = useMutation<AcknowledgeCanopyTermsOfServiceResponse>(
    ACKNOWLEDGE_CANOPY_TERMS_OF_SERVICE,
    {
      variables: {
        source: TERMS_OF_SERVICE_SOURCE,
        version: CURRENT_CANOPY_TOS_VERSION,
      },
      onError: () => sethasError(true),
    },
  );

  const [
    acknowledgeCoveredTermsOfServiceMutation,
    { loading: isLoadingCoveredTermsOfService },
  ] = useMutation<AcknowledgeCoveredTermsOfServiceResponse>(
    ACKNOWLEDGE_COVERED_TERMS_OF_SERVICE,
    {
      onError: () => sethasError(true),
    },
  );

  const handleChecked = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setChecked(event.target.checked);
  };

  const handleContinue = async () => {
    segmentTrack(
      'NW Plus Insurance Canopy Flow Consent Screen Continue Button Clicked',
      {
        is_nw_plus_member: isNerdWalletPlusMember,
        is_platform_native: isPlatformNative,
      },
    );

    sethasError(false);

    const coveredTermsOfServiceData =
      await acknowledgeCoveredTermsOfServiceMutation();

    const canopyTermsOfServiceData =
      await acknowledgeCanopyTermsOfServiceMutation();

    const hasCanopyConsent =
      canopyTermsOfServiceData?.data?.acknowledgeCanopyTermsOfServiceAgreement
        ?.success;

    const hasCoveredConsent =
      coveredTermsOfServiceData?.data?.acknowledgeCoveredTermsOfService
        ?.success;

    if (hasCanopyConsent && hasCoveredConsent) {
      dispatch({
        type: Actions.CHECK_CONSENT,
        payload: {
          hasGivenConsent: true,
        },
      });
    }
  };

  return (
    <Box className={styles.consentWrapper}>
      <When condition={hasError}>
        <SegmentImpression
          eventName="NW Plus Insurance Canopy Flow Consent Screen Error Viewed"
          eventProps={{ is_nw_plus_member: isNerdWalletPlusMember }}
        >
          <Box className={styles.errorContainer}>
            <ErrorIcon color={colors.yellow} size={16} textAligned />
            <Text tone="light" size="small">
              Hm. Looks like there was a technical issue. Try again in a bit.
            </Text>
          </Box>
        </SegmentImpression>
      </When>

      <Box className={styles.copyContainer}>
        <Title size="small" className={styles.title} ref={titleRef}>
          Please agree to these terms before linking with Canopy Connect and
          Covered
        </Title>
        <Box marginTop={2}>
          <Stack>
            <Text component="p" size="small">
              By checking “I agree”, you hereby consent to Canopy’s disclosure
              and NerdWallet’s collection of your personal information,
              including details of any policies contained in your linked
              insurance accounts. You further consent to NerdWallet’s disclosure
              of your information to Covered to generate insurance quotes and
              other insurance related offers on your behalf. You acknowledge
              that you have read and consent to {`NerdWallet's`}{' '}
              <Link
                href="https://www.nerdwallet.com/p/terms-of-use"
                target="_blank"
                decorated
              >
                Terms of Use
              </Link>{' '}
              (including the{' '}
              <Link
                href="https://www.nerdwallet.com/lp/nerdwallet-plus/terms-of-service"
                target="_blank"
                decorated
              >
                NerdWallet+ Terms
              </Link>
              ) and that the processing of your data by NerdWallet will be
              governed by its{' '}
              <Link
                href="https://www.nerdwallet.com/p/terms-of-use"
                target="_blank"
                decorated
              >
                Terms of Use
              </Link>{' '}
              and{' '}
              <Link
                href="https://www.nerdwallet.com/p/privacy-policy"
                target="_blank"
                decorated
              >
                Privacy Policy
              </Link>
              . You authorize NerdWallet and its subsidiaries to use your
              information for the purposes identified in its{' '}
              <Link href="https://www.nerdwallet.com/p/privacy-policy">
                Privacy Policy
              </Link>
              , including but not limited to, determining your eligibility for
              other products or services offered by NerdWallet or its partners
              and marketing those products and services to you.
            </Text>
          </Stack>
        </Box>
        <Box
          display={{ desktop: 'none', mobile: 'block' }}
          marginTop={1}
          marginBottom="3-half"
        >
          <Link onClick={() => titleRef.current.scrollIntoView()} size={1}>
            Scroll to top
          </Link>
        </Box>
      </Box>

      <Box marginTop="3-half">
        <CheckboxOption
          label="I agree"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            handleChecked(event)
          }
        />

        <Box marginTop={3}>
          <Button
            block
            loading={
              isLoadingCoveredTermsOfService || isLoadingCanopyTermsOfService
            }
            disabled={
              checked === false ||
              isLoadingCoveredTermsOfService ||
              isLoadingCanopyTermsOfService
            }
            onClick={handleContinue}
          >
            Continue
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default TermsOfServiceScreen;
